import React, { useEffect, useRef, useState } from "react";
import { API, authedFetch } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import { uploadWithRetry } from "../lib/uploadWithRetry";
import {
  isPendingPath, newPendingKey, savePending, getPending, removePending,
  notifyPendingChanged,
} from "../lib/photoQueue";
import useFileDropzone from "../lib/useFileDropzone";
import { ImagePlus, X, Loader2, Pencil, RotateCw, CloudOff } from "lucide-react";
import { openLightbox } from "./Lightbox";
import PhotoAnnotator from "./PhotoAnnotator";

/**
 * SinglePhotoCell — compact uploader for a single photo (used in damage table
 * rows). Resilient to bad networks: failed uploads are persisted to IndexedDB
 * and the cell renders the LOCAL blob until the photo gets flushed.
 *
 * @param value: string path (or "pending::<uuid>" or null)
 * @param onChange: (newPath|null) => void
 * @param size: "sm" | "md"
 * @param readOnly: bool
 */
export default function SinglePhotoCell({ value, onChange, size = "sm", readOnly = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState("");
  const doUploadRef = useRef(null);
  const pending = isPendingPath(value);

  // Drop-zone hook (top-level, called unconditionally to keep hook order stable)
  const { handlers: dropHandlers, isOver } = useFileDropzone({
    onFiles: (files) => { doUploadRef.current?.(files[0]); },
    accept: ["png", "jpg", "jpeg", "webp", "heic", "gif"],
    multiple: false,
    disabled: readOnly || uploading || !!value,
  });

  // Resolve preview — either local IDB blob (pending) or authed remote fetch
  useEffect(() => {
    let cancelled = false;
    let localUrl = null;
    if (!value) {
      setBlobUrl((_p) => null);
      return undefined;
    }
    async function load() {
      try {
        let blob = null;
        if (isPendingPath(value)) {
          const rec = await getPending(value);
          blob = rec?.blob || null;
        } else {
          const res = await authedFetch(`${API}/files/${value}`);
          if (res.ok) blob = await res.blob();
        }
        if (!blob || cancelled) return;
        localUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl((_p) => localUrl);
      } catch { /* ignore */ }
    }
    load();
    return () => {
      cancelled = true;
      if (localUrl) { try { URL.revokeObjectURL(localUrl); } catch { /* */ } }
    };
  }, [value]);

  // Listen for global flush events that swap "pending::*" → real path
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.key === value && e.detail?.newPath) {
        onChange(e.detail.newPath);
      }
    };
    window.addEventListener("uc:pending-photo-flushed", handler);
    return () => window.removeEventListener("uc:pending-photo-flushed", handler);
  }, [value, onChange]);

  const dim = size === "sm" ? "w-14 h-14" : "w-24 h-24";

  const pick = () => inputRef.current?.click();

  const doUpload = async (file) => {
    setError("");
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      try {
        const data = await uploadWithRetry(compressed);
        onChange(data.path);
      } catch {
        // All retries failed → persist locally and surface a pending placeholder
        const key = newPendingKey();
        await savePending(key, compressed, { name: compressed.name || file.name || "foto.jpg" });
        notifyPendingChanged();
        onChange(key);
        setError("Offline · salvata sul dispositivo");
      }
    } catch (err) {
      setError(err?.message || "Upload fallito");
    }
    setUploading(false);
  };
  useEffect(() => { doUploadRef.current = doUpload; });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await doUpload(file);
  };

  const retryNow = async () => {
    if (!isPendingPath(value)) return;
    const rec = await getPending(value);
    if (!rec) { onChange(null); return; }
    setError("");
    setUploading(true);
    try {
      const data = await uploadWithRetry(rec.blob, { filename: rec.name });
      await removePending(value);
      notifyPendingChanged();
      onChange(data.path);
    } catch {
      setError("Ancora offline · riprova fra poco");
    }
    setUploading(false);
  };

  const removeMe = async () => {
    if (isPendingPath(value)) {
      await removePending(value);
      notifyPendingChanged();
    }
    onChange(null);
  };

  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const handleAnnotated = async (blob) => {
    setAnnotatorOpen(false);
    const file = new File([blob], "annotata.jpg", { type: "image/jpeg" });
    await doUpload(file);
  };

  if (value) {
    return (
      <div className={`relative ${dim} border-[1.5px] border-black bg-neutral-100 overflow-hidden rounded-md`}>
        {blobUrl ? (
          <button
            type="button"
            onClick={() => !pending && openLightbox(value, "Foto")}
            className="w-full h-full"
            title={pending ? "Foto in coda (offline)" : "Apri foto"}
          >
            <img src={blobUrl} alt="foto" className={`w-full h-full object-cover ${pending ? "" : "cursor-zoom-in"}`} />
          </button>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />
          </div>
        )}
        {pending && (
          <div className="absolute inset-x-0 bottom-0 bg-[#ffcc00] text-black text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 flex items-center justify-center gap-1" data-testid="single-photo-pending">
            <CloudOff className="w-3 h-3" />
            In coda
          </div>
        )}
        {!readOnly && (
          <>
            <button
              type="button"
              onClick={removeMe}
              className="absolute top-0 right-0 bg-black text-white w-5 h-5 flex items-center justify-center hover:bg-[#ff3b30] rounded-bl-md"
              aria-label="Rimuovi"
              data-testid="single-photo-remove"
            >
              <X className="w-3 h-3" />
            </button>
            {pending ? (
              <button
                type="button"
                onClick={retryNow}
                disabled={uploading}
                className="absolute bottom-5 right-0 bg-[#ff5a00] text-white w-5 h-5 flex items-center justify-center hover:bg-[#ff7a30] rounded-tl-md disabled:opacity-60"
                aria-label="Riprova upload"
                data-testid="single-photo-retry"
                title="Riprova upload"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
              </button>
            ) : blobUrl && (
              <button
                type="button"
                onClick={() => setAnnotatorOpen(true)}
                className="absolute bottom-0 right-0 bg-[#ff5a00] text-white w-5 h-5 flex items-center justify-center hover:bg-[#ff7a30] rounded-tl-md"
                aria-label="Modifica con pennarello"
                data-testid="single-photo-edit"
                title="Modifica con pennarello"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </>
        )}
        {annotatorOpen && blobUrl && (
          <PhotoAnnotator
            src={blobUrl}
            onSave={handleAnnotated}
            onCancel={() => setAnnotatorOpen(false)}
          />
        )}
      </div>
    );
  }

  if (readOnly) return <div className={`${dim} border-[1.5px] border-dashed border-neutral-300 rounded-md`} />;

  return (
    <>
      <button
        type="button"
        onClick={pick}
        {...dropHandlers}
        disabled={uploading}
        className={`${dim} border-[1.5px] border-dashed flex items-center justify-center disabled:opacity-60 rounded-md transition-colors ${
          isOver ? "border-[#ff5a00] bg-[#fff7f0] ring-2 ring-[#ff5a00]" : "border-black bg-white hover:bg-[#fff7f0]"
        }`}
        data-testid="single-photo-add"
        title={error || "Aggiungi foto"}
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ImagePlus className="w-4 h-4" />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
      />
    </>
  );
}
